import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Building2, Loader } from 'lucide-react';
import apiClient from '@/services/api';

export default function ApproveDevice() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid link, no token found.');
      return;
    }
    apiClient
      .get(`/auth/devices/approve-by-token/${token}`)
      .then((res) => {
        setStatus('success');
        setMessage(res.data?.message ?? 'Device approved successfully. You can now log in.');
      })
      .catch((err) => {
        setStatus('error');
        setMessage(
          err?.response?.data?.message ??
            'This link is invalid or has expired. Please log in again to receive a new link.'
        );
      });
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 to-secondary-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-secondary-900">Baytarc</h1>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-secondary-200 p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader className="w-12 h-12 text-primary-600 animate-spin mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">
                Approving your device…
              </h2>
              <p className="text-secondary-500">Please wait a moment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">Device Approved</h2>
              <p className="text-secondary-600 mb-6">{message}</p>
              <Link
                to="/login"
                className="inline-block px-6 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 transition-colors"
              >
                Go to Login
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-secondary-900 mb-2">
                Link Expired or Invalid
              </h2>
              <p className="text-secondary-600 mb-6">{message}</p>
              <Link
                to="/login"
                className="inline-block px-6 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 transition-colors"
              >
                Back to Login
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
